import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiX, FiUser, FiBriefcase, FiMail, FiPhone, FiCheck, FiUpload, FiTrash2, FiMapPin, FiClock, FiLock, FiArrowRight, FiImage } from 'react-icons/fi';
import './ServiceProviderApplication.css';
import { createService, userGetServices, checkExistingApplication } from '../../../api/user/serviceProvider/service-provider';

const CONTACT_NUMBER_MAX_LENGTH = 9; // adjust to match your local number length

const ServiceProviderApplication = ({ toggleUpdatePanel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tell: '',
    experience: '',
    service: '',
    location: '',
    termsAccepted: false
  });
  const [countryCode, setCountryCode] = useState('+237');

  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [existingApp, setExistingApp] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCountryCodeChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d+]/g, '');
    if (value.indexOf('+') > 0) {
      value = '+' + value.replace(/\+/g, '');
    }
    value = value.slice(0, 5);
    setCountryCode(value);
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, CONTACT_NUMBER_MAX_LENGTH);
    setFormData(prev => ({ ...prev, tell: digitsOnly }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('service_provider_application.invalid_image_type'));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('service_provider_application.image_too_large'));
      return;
    }

    setError('');
    setThumbnail(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchServices = async () => {
    setIsLoadingServices(true);
    setError('');
    try {
      const response = await userGetServices();
      console.log('Services response:', response);

      if (response?.success) {
        const receivedServices = response.services || [];
        setServices(Array.isArray(receivedServices) ? receivedServices : []);
      } else {
        setError(response?.error || t('service_provider_application.failed_load_services'));
      }
    } catch (err) {
      console.error('Service fetch error:', err);
      setError(t('service_provider_application.failed_load_services_later'));
      setServices([]);
    } finally {
      setIsLoadingServices(false);
    }
  };

  useEffect(() => {
    const checkExisting = async () => {
      setCheckingExisting(true);
      try {
        const result = await checkExistingApplication();
        if (result.hasExisting) {
          setExistingApp(result);
        }
      } catch (err) {
        console.error('Error checking existing application:', err);
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
  }, []);

  useEffect(() => {
    if (!existingApp) {
      fetchServices();
    }
  }, [existingApp]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') toggleUpdatePanel();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [toggleUpdatePanel]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.tell || formData.tell.length < 8) {
      setError(t('service_provider_application.invalid_phone'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create FormData to handle file upload
      const submissionData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        submissionData.append(key, formData[key]);
      });
      submissionData.append('countryCode', countryCode);

      // Append thumbnail if selected
      if (thumbnail) {
        submissionData.append('thumbnail', thumbnail);
      }

      // Use createService API call
      const response = await createService(submissionData);
      console.log('Submission response:', response);

      if (response?.success) {
        setSubmitSuccess(true);

        setTimeout(() => {
          toggleUpdatePanel();
        }, 2200);

      } else {
        setError(response?.error || t('service_provider_application.submission_failed'));
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError(t('service_provider_application.unexpected_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="spa-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) toggleUpdatePanel(); }}>
      <div className="spa-modal-container" role="dialog" aria-modal="true" aria-label={t('service_provider_application.aria_label')}>
        <button className="spa-close-btn" onClick={toggleUpdatePanel} aria-label={t('service_provider_application.close')}>
          <FiX size={20} />
        </button>

        {checkingExisting ? (
          <div className="spa-checking-state">
            <div className="spa-checking-spinner" />
            <p>{t('service_provider_application.checking_application')}</p>
          </div>
        ) : existingApp?.approved ? (
          <div className="spa-approved-state">
            <div className="spa-success-icon">
              <FiCheck size={44} />
            </div>
            <h3>{t('service_provider_application.already_approved_title')}</h3>
            <p>{t('service_provider_application.already_approved_desc')}</p>
            <button className="spa-close-success-btn" onClick={toggleUpdatePanel}>
              {t('service_provider_application.got_it')}
            </button>
          </div>
        ) : existingApp?.pending ? (
          <div className="spa-pending-state">
            <div className="spa-pending-animation">
              <div className="spa-pending-ring spa-pending-ring-1" />
              <div className="spa-pending-ring spa-pending-ring-2" />
              <div className="spa-pending-ring spa-pending-ring-3" />
              <div className="spa-pending-icon">
                <FiClock size={32} />
              </div>
            </div>
            <h3>{t('service_provider_application.already_applied_title')}</h3>
            <p>{t('service_provider_application.already_applied_desc')}</p>
            <div className="spa-pending-status">
              <span className="spa-pending-dot" />
              {t('service_provider_application.review_in_progress')}
            </div>
            <div className="spa-pending-timeline">
              <div className="spa-timeline-step spa-timeline-done">
                <div className="spa-timeline-circle">
                  <FiCheck size={14} />
                </div>
                <span>{t('service_provider_application.submitted')}</span>
              </div>
              <div className="spa-timeline-step spa-timeline-active">
                <div className="spa-timeline-circle">
                  <div className="spa-timeline-pulse" />
                  <FiClock size={14} />
                </div>
                <span>{t('service_provider_application.under_review')}</span>
              </div>
              <div className="spa-timeline-step spa-timeline-pending">
                <div className="spa-timeline-circle">
                  <FiCheck size={14} />
                </div>
                <span>{t('service_provider_application.approved')}</span>
              </div>
            </div>
            <button className="spa-close-success-btn" onClick={toggleUpdatePanel}>
              {t('service_provider_application.got_it')}
            </button>
          </div>
        ) : existingApp?.declined ? (
          <div className="spa-declined-state">
            <div className="spa-declined-icon">
              <FiX size={44} />
            </div>
            <h3>{t('service_provider_application.application_declined_title')}</h3>
            <p>{t('service_provider_application.application_declined_desc')}</p>
            <button className="spa-reapply-btn" onClick={() => setExistingApp(null)}>
              {t('service_provider_application.reapply')}
            </button>
          </div>
        ) : submitSuccess ? (
          <div className="spa-success-message">
            <div className="spa-success-icon">
              <FiCheck size={44} />
            </div>
            <h3>{t('service_provider_application.application_submitted')}</h3>
            <p>{t('service_provider_application.application_submitted_desc')}</p>
            <div className="spa-success-steps">
              <span><FiCheck size={13} /> {t('service_provider_application.application_received')}</span>
              <span><FiCheck size={13} /> {t('service_provider_application.under_review')}</span>
              <span><FiClock size={13} /> {t('service_provider_application.response_within_48h')}</span>
            </div>
            <button
              className="spa-close-success-btn"
              onClick={toggleUpdatePanel}
            >
              {t('service_provider_application.done')}
            </button>
          </div>
        ) : (
          <>
            <div className="spa-modal-header">
              <div className="spa-header-icon">
                <FiBriefcase size={26} />
              </div>
              <h2>{t('service_provider_application.title')}</h2>
              <p>{t('service_provider_application.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="spa-form" encType="multipart/form-data">
              {/* Thumbnail Upload Section */}
              <div className="spa-form-group">
                <label className="spa-form-label" htmlFor="thumbnail">
                  <FiImage className="spa-label-icon" />
                  {t('service_provider_application.service_thumbnail')}
                </label>
                <div className="spa-thumbnail-upload-container">
                  <input
                    type="file"
                    id="thumbnail"
                    name="thumbnail"
                    ref={fileInputRef}
                    onChange={handleThumbnailChange}
                    accept="image/*"
                    className="spa-thumbnail-input"
                  />

                  {thumbnailPreview ? (
                    <div className="spa-thumbnail-preview-container">
                      <img
                        src={thumbnailPreview}
                        alt={t('service_provider_application.thumbnail_preview')}
                        className="spa-thumbnail-preview"
                      />
                      <button
                        type="button"
                        className="spa-remove-thumbnail"
                        onClick={removeThumbnail}
                        aria-label={t('service_provider_application.remove_image')}
                      >
                        <FiTrash2 size={16} />
                      </button>
                      <div className="spa-preview-actions">
                        <label htmlFor="thumbnail" className="spa-change-thumbnail">
                          <FiUpload size={13} /> {t('service_provider_application.change')}
                        </label>
                        <button type="button" className="spa-delete-thumbnail" onClick={removeThumbnail}>
                          <FiTrash2 size={13} /> {t('service_provider_application.remove')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="thumbnail" className="spa-thumbnail-upload-area">
                      <div className="spa-upload-icon">
                        <FiUpload size={22} />
                      </div>
                      <span className="spa-upload-title">{t('service_provider_application.upload_title')}</span>
                      <span className="spa-upload-hint">{t('service_provider_application.upload_hint')}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Service type */}
              <div className="spa-form-group">
                <label className="spa-form-label" htmlFor="service">
                  <FiBriefcase className="spa-label-icon" />
                  {t('service_provider_application.service_type')} <span className="spa-required">*</span>
                </label>
                {isLoadingServices ? (
                  <div className="spa-loading-services">{t('service_provider_application.loading_services')}</div>
                ) : services.length === 0 ? (
                  <div className="spa-no-services">
                    <FiBriefcase size={18} />
                    {t('service_provider_application.no_services')}
                  </div>
                ) : (
                  <div className="spa-input-wrap">
                    <FiBriefcase className="spa-field-icon" />
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="spa-select"
                    >
                      <option value="">{t('service_provider_application.select_service')}</option>
                      {services.map((service) => (
                        <option key={service._id} value={service._id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Business info */}
              <div className="spa-grid-2">
                <div className="spa-form-group">
                  <label className="spa-form-label" htmlFor="name">
                    <FiUser className="spa-label-icon" />
                    {t('service_provider_application.business_name')} <span className="spa-optional">{t('service_provider_application.optional')}</span>
                  </label>
                  <div className="spa-input-wrap">
                    <FiUser className="spa-field-icon" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      placeholder="Paul Electronics"
                      onChange={handleChange}
                      className="spa-input"
                    />
                  </div>
                </div>

                <div className="spa-form-group">
                  <label className="spa-form-label" htmlFor="email">
                    <FiMail className="spa-label-icon" />
                    {t('service_provider_application.business_email')} <span className="spa-optional">{t('service_provider_application.optional')}</span>
                  </label>
                  <div className="spa-input-wrap">
                    <FiMail className="spa-field-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      placeholder="john@gmail.com"
                      onChange={handleChange}
                      className="spa-input"
                    />
                  </div>
                </div>
              </div>

              {/* Location + experience */}
              <div className="spa-grid-2">
                <div className="spa-form-group">
                  <label className="spa-form-label" htmlFor="location">
                    <FiMapPin className="spa-label-icon" />
                    {t('service_provider_application.location')} <span className="spa-required">*</span>
                  </label>
                  <div className="spa-input-wrap">
                    <FiMapPin className="spa-field-icon" />
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      placeholder={t('service_provider_application.location_placeholder')}
                      onChange={handleChange}
                      className="spa-input"
                    />
                  </div>
                </div>

                <div className="spa-form-group">
                  <label className="spa-form-label" htmlFor="experience">
                    <FiClock className="spa-label-icon" />
                    {t('service_provider_application.years_of_experience')} <span className="spa-required">*</span>
                  </label>
                  <div className="spa-input-wrap">
                    <FiClock className="spa-field-icon" />
                    <input
                      type="number"
                      id="experience"
                      name="experience"
                      min="0"
                      max="50"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      placeholder={t('service_provider_application.experience_placeholder')}
                      className="spa-input"
                    />
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="spa-form-group">
                <label className="spa-form-label" htmlFor="tell">
                  <FiPhone className="spa-label-icon" />
                  {t('service_provider_application.phone_number')} <span className="spa-required">*</span>
                </label>
                <div className="spa-phone-input-group">
                  <input
                    type="text"
                    className="spa-country-code-input"
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    placeholder="+237"
                    maxLength={5}
                    aria-label={t('service_provider_application.country_code')}
                    required
                  />
                  <span className="spa-phone-divider" />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="spa-phone-number-input"
                    id="tell"
                    name="tell"
                    value={formData.tell}
                    onChange={handlePhoneChange}
                    placeholder="6XXXXXXXX"
                    maxLength={CONTACT_NUMBER_MAX_LENGTH}
                    required
                  />
                </div>
                <span className="spa-char-count">
                  {t('service_provider_application.char_count')}
                </span>
              </div>

              {error && (
                <div className="spa-error-message">
                  <FiX size={16} />
                  {error}
                </div>
              )}

              <div className="spa-terms-checkbox">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="termsAccepted">
                  {t('service_provider_application.terms_agreement')} <a href="/terms" target="_blank" rel="noopener noreferrer">{t('service_provider_application.terms_of_service')}</a> {t('service_provider_application.and')} <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('service_provider_application.privacy_policy')}</a>
                </label>
              </div>

              <div className="spa-form-footer">
                <button
                  type="submit"
                  className="spa-submit-btn"
                  disabled={isSubmitting || !formData.termsAccepted || (services.length === 0 && !isLoadingServices)}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spa-btn-spinner"></span>
                      {t('service_provider_application.submitting')}
                    </>
                  ) : (
                    <>
                      {t('service_provider_application.apply_now')}
                      <FiArrowRight size={17} />
                    </>
                  )}
                </button>

                <p className="spa-secure-note">
                  <FiLock size={12} />
                  {t('service_provider_application.secure_note')}
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ServiceProviderApplication;
